/**
 * Project board layout: the sections a board shows, their order, and the manual
 * card order inside each one (project_board_layouts).
 *
 * This is shared by the whole team. Which section a task sits in already lived on
 * the task; the section list used to live in localStorage, so sections one person
 * added never reached anyone else. The old local layout is still read once, to
 * publish it for the team the next time its author opens the board.
 */

import { API_BASE } from '../config/apiBase';
import { PROJECT_BOARD_FIRST_COLUMN_ID } from '../constants/projectBoard';

export interface ProjectBoardColumnDef {
  id: string;
  title: string;
}

/** Per-column manual ordering of task ids (set when someone drags cards). */
export type ProjectBoardItemOrder = Record<string, number[]>;

export interface ProjectBoardLayout {
  columns: ProjectBoardColumnDef[];
  itemOrder: ProjectBoardItemOrder;
}

export function createDefaultProjectBoardColumns(): ProjectBoardColumnDef[] {
  return [
    { id: PROJECT_BOARD_FIRST_COLUMN_ID, title: 'To Do' },
    { id: 'col-doing', title: 'Doing' },
    { id: 'col-done', title: 'Done' },
  ];
}

function parseColumns(raw: unknown): ProjectBoardColumnDef[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const out: ProjectBoardColumnDef[] = [];
  for (const entry of raw) {
    if (entry == null || typeof entry !== 'object') continue;
    const id = String((entry as { id?: unknown }).id ?? '').trim();
    const title = String((entry as { title?: unknown }).title ?? '').trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push({ id, title: title || 'Untitled' });
  }
  return out;
}

function parseItemOrder(raw: unknown): ProjectBoardItemOrder {
  const out: ProjectBoardItemOrder = {};
  if (!raw || typeof raw !== 'object') return out;
  for (const [columnId, ids] of Object.entries(raw as Record<string, unknown>)) {
    if (!Array.isArray(ids)) continue;
    out[columnId] = ids.map((n) => Number(n)).filter((n) => Number.isInteger(n));
  }
  return out;
}

/** null means the project has no published layout yet, so the app defaults apply. */
export async function fetchProjectBoardLayout(
  projectId: number,
  viewerUserId: number
): Promise<ProjectBoardLayout | null> {
  const res = await fetch(
    `${API_BASE}/api/projects/${projectId}/board-layout?viewerUserId=${encodeURIComponent(String(viewerUserId))}`
  );
  if (!res.ok) throw new Error('Failed to load the board layout');
  const data = await res.json();
  const raw = data?.layout;
  if (!raw) return null;
  const columns = parseColumns(raw.columns);
  if (columns.length === 0) return null;
  return { columns, itemOrder: parseItemOrder(raw.item_order) };
}

export async function saveProjectBoardLayout(
  projectId: number,
  userId: number,
  layout: ProjectBoardLayout
): Promise<ProjectBoardLayout> {
  const res = await fetch(`${API_BASE}/api/projects/${projectId}/board-layout`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId, columns: layout.columns, item_order: layout.itemOrder }),
  });
  if (!res.ok) {
    let message = 'Failed to save the board layout';
    try {
      const body = await res.json();
      if (body?.error) message = String(body.error);
    } catch {
      /* keep the fallback message */
    }
    throw new Error(message);
  }
  const data = await res.json();
  const columns = parseColumns(data?.layout?.columns);
  return {
    columns: columns.length > 0 ? columns : layout.columns,
    itemOrder: parseItemOrder(data?.layout?.item_order),
  };
}

const LEGACY_STORAGE_PREFIX = 'techforce_project_board_v1_';
const COLLAPSED_STORAGE_PREFIX = 'techforce_project_board_collapsed_v1_';

/**
 * The pre-sync layout from this browser. Used to seed the shared layout, and to
 * name sections that only exist as an id on a task.
 */
export function loadProjectBoardLayoutLegacy(projectId: number): ProjectBoardLayout | null {
  try {
    const raw = localStorage.getItem(`${LEGACY_STORAGE_PREFIX}${projectId}`);
    if (!raw) return null;
    const data = JSON.parse(raw) as { columns?: unknown; itemOrder?: unknown };
    const columns = parseColumns(data?.columns);
    if (columns.length === 0) return null;
    return { columns, itemOrder: parseItemOrder(data?.itemOrder) };
  } catch {
    return null;
  }
}

/** Only call once the layout is safely on the server. */
export function clearProjectBoardLayoutLegacy(projectId: number): void {
  try {
    localStorage.removeItem(`${LEGACY_STORAGE_PREFIX}${projectId}`);
  } catch {
    /* ignore */
  }
}

/** Collapsed sections stay personal: folding a column is a view preference, not a board change. */
export function loadProjectBoardCollapsed(projectId: number): string[] {
  try {
    const raw = localStorage.getItem(`${COLLAPSED_STORAGE_PREFIX}${projectId}`);
    if (raw) {
      const parsed = JSON.parse(raw) as unknown;
      if (Array.isArray(parsed)) return parsed.filter((x): x is string => typeof x === 'string');
    }
    const legacy = localStorage.getItem(`${LEGACY_STORAGE_PREFIX}${projectId}`);
    if (!legacy) return [];
    const data = JSON.parse(legacy) as { collapsedIds?: unknown };
    return Array.isArray(data?.collapsedIds)
      ? data.collapsedIds.filter((x): x is string => typeof x === 'string')
      : [];
  } catch {
    return [];
  }
}

export function saveProjectBoardCollapsed(projectId: number, collapsedIds: string[]): void {
  try {
    localStorage.setItem(`${COLLAPSED_STORAGE_PREFIX}${projectId}`, JSON.stringify(collapsedIds));
  } catch {
    /* ignore quota */
  }
}
